/**
 * Game Menu - HTML Overlay UI (ESC Menu)
 * Renders as DOM elements over the game canvas.
 * Same pattern as SkillEditorHTML.ts and InventoryHTML.ts
 */

import { Player } from '@client/entities/Player';
import { getAudioManager } from '@client/audio/AudioManager';
import { setLocale, getCurrentLocale } from '@locales/index';

// ========================
// Types
// ========================

type TabId = 'character' | 'passives' | 'quests' | 'map' | 'settings' | 'save';

interface TabDef {
  id: TabId;
  label: string;
}

// ========================
// CSS Styles (injected once)
// ========================

const CSS = `
.gm-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  font-family: 'Segoe UI', Tahoma, sans-serif;
  color: #f4e8d4;
  user-select: none;
}

.gm-panel {
  background: linear-gradient(180deg, #1a1510, #0d0a08);
  border: 2px solid #3a3530;
  border-radius: 10px;
  width: 85vw;
  height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.gm-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #3a3530;
  flex-shrink: 0;
}
.gm-header h2 {
  color: #ffd700;
  font-size: 20px;
  margin: 0;
}
.gm-close {
  background: #3d3228;
  border: 1px solid #5a4a3a;
  color: #f4e8d4;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.gm-close:hover { background: #5a4a3a; }

/* Tab bar */
.gm-tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid #3a3530;
  flex-shrink: 0;
  padding: 0 12px;
}
.gm-tab {
  background: none;
  border: none;
  color: #8a7e6e;
  font-size: 14px;
  padding: 10px 18px;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.15s;
  font-family: inherit;
}
.gm-tab:hover { color: #f4e8d4; }
.gm-tab.active {
  color: #ffd700;
  border-bottom-color: #ffd700;
}

/* Content area */
.gm-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px;
  min-height: 0;
}

/* Scrollbar */
.gm-content::-webkit-scrollbar { width: 6px; }
.gm-content::-webkit-scrollbar-track { background: #1a1815; }
.gm-content::-webkit-scrollbar-thumb { background: #3a3530; border-radius: 3px; }

/* Character tab - two columns */
.gm-char-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}
.gm-section {
  background: #151210;
  border: 1px solid #2a2520;
  border-radius: 8px;
  padding: 14px;
}
.gm-section-title {
  color: #ffd700;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
}
.gm-stat-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  border-bottom: 1px solid #1e1a15;
  font-size: 13px;
}
.gm-stat-row:last-child { border-bottom: none; }
.gm-stat-label { color: #a09080; }
.gm-stat-value { color: #f4e8d4; font-weight: bold; }

/* XP bar */
.gm-xp-bar {
  width: 100%;
  height: 8px;
  background: #1a1815;
  border-radius: 4px;
  overflow: hidden;
  margin: 6px 0;
}
.gm-xp-fill {
  height: 100%;
  background: linear-gradient(90deg, #4a8fd4, #7dc4ff);
  border-radius: 4px;
  transition: width 0.3s;
}

/* Resistance bars */
.gm-res-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
}
.gm-res-label {
  width: 70px;
  color: #a09080;
}
.gm-res-bar {
  flex: 1;
  height: 14px;
  background: #1a1815;
  border-radius: 3px;
  overflow: hidden;
  position: relative;
}
.gm-res-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s;
}
.gm-res-val {
  width: 55px;
  text-align: right;
  color: #f4e8d4;
  font-weight: bold;
  font-size: 12px;
}

/* Quest styles */
.gm-quest {
  background: #151210;
  border: 1px solid #2a2520;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 8px;
}
.gm-quest-title {
  color: #ffd700;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 6px;
}
.gm-quest-obj {
  font-size: 12px;
  color: #a09080;
  margin-left: 12px;
  padding: 2px 0;
}
.gm-quest.completed {
  opacity: 0.5;
  border-color: #1e1a15;
}
.gm-quest.completed .gm-quest-title { color: #6a6050; }
.gm-progress-bar {
  width: 100%;
  height: 6px;
  background: #1a1815;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 4px;
}
.gm-progress-fill {
  height: 100%;
  background: #ffd700;
  border-radius: 3px;
}

/* Settings */
.gm-setting-group {
  background: #151210;
  border: 1px solid #2a2520;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 12px;
}
.gm-setting-group-title {
  color: #ffd700;
  font-size: 13px;
  font-weight: bold;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
}
.gm-slider-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 13px;
}
.gm-slider-label {
  width: 120px;
  color: #a09080;
}
.gm-slider-val {
  width: 40px;
  text-align: right;
  color: #f4e8d4;
  font-weight: bold;
}
input[type="range"].gm-range {
  flex: 1;
  -webkit-appearance: none;
  appearance: none;
  height: 6px;
  background: #2a2520;
  border-radius: 3px;
  outline: none;
}
input[type="range"].gm-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ffd700;
  cursor: pointer;
  border: 2px solid #3a3530;
}
input[type="range"].gm-range::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ffd700;
  cursor: pointer;
  border: 2px solid #3a3530;
}

/* Toggle buttons */
.gm-toggle-group {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}
.gm-toggle-btn {
  background: #1a1815;
  border: 1px solid #3a3530;
  color: #8a7e6e;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-family: inherit;
  transition: all 0.15s;
}
.gm-toggle-btn.active {
  background: #3a3530;
  border-color: #ffd700;
  color: #ffd700;
}
.gm-toggle-btn:hover { border-color: #5a4a3a; color: #f4e8d4; }

/* Checkbox row */
.gm-check-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 13px;
  color: #a09080;
  cursor: pointer;
}
.gm-check-row input[type="checkbox"] {
  accent-color: #ffd700;
  width: 16px;
  height: 16px;
}

/* Save slots */
.gm-save-slots {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.gm-save-slot {
  background: #151210;
  border: 1px solid #2a2520;
  border-radius: 8px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.gm-save-slot-title {
  color: #ffd700;
  font-size: 14px;
  font-weight: bold;
}
.gm-save-slot-info {
  font-size: 12px;
  color: #8a7e6e;
  line-height: 1.6;
}
.gm-save-slot-empty {
  color: #4a4030;
  font-style: italic;
  font-size: 13px;
}
.gm-save-slot-actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}
.gm-btn {
  background: #3d3228;
  border: 1px solid #5a4a3a;
  color: #f4e8d4;
  padding: 6px 14px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-family: inherit;
  transition: all 0.15s;
}
.gm-btn:hover { background: #5a4a3a; border-color: #ffd700; }
.gm-btn.primary {
  background: #4a3a20;
  border-color: #ffd700;
  color: #ffd700;
}
.gm-btn.primary:hover { background: #5a4a2a; }

/* Placeholder text */
.gm-placeholder {
  text-align: center;
  padding: 40px 20px;
  color: #5a4a3a;
  font-size: 16px;
}
.gm-placeholder-sub {
  font-size: 13px;
  color: #3a3530;
  margin-top: 8px;
}
`;

// ========================
// Game Menu HTML UI
// ========================

export class GameMenuHTML {
  private player: Player;
  private container: HTMLDivElement | null = null;
  private _isOpen = false;
  private styleInjected = false;
  private activeTab: TabId = 'character';

  private saveCallback: ((slot: number) => void) | null = null;
  private loadCallback: ((slot: number) => void) | null = null;

  // Passive tree camera state (persists across re-renders)
  private ptScrollX = 0;
  private ptScrollY = 0;
  private ptZoom = 0.6;
  private ptInitialized = false;

  constructor(player: Player) {
    this.player = player;
  }

  open(tabId?: string): void {
    if (tabId && this.isValidTab(tabId)) {
      this.activeTab = tabId as TabId;
    }
    this._isOpen = true;
    this.render();
  }

  close(): void {
    this._isOpen = false;
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }

  toggle(tabId?: string): void {
    if (this._isOpen) {
      this.close();
    } else {
      this.open(tabId);
    }
  }

  isVisible(): boolean { return this._isOpen; }

  setSaveCallback(cb: (slot: number) => void): void {
    this.saveCallback = cb;
  }

  setLoadCallback(cb: (slot: number) => void): void {
    this.loadCallback = cb;
  }

  // ========================
  // Private - Rendering
  // ========================

  private isValidTab(id: string): boolean {
    return ['character', 'passives', 'quests', 'map', 'settings', 'save'].includes(id);
  }

  private getTabs(): TabDef[] {
    return [
      { id: 'character', label: 'Karakter' },
      { id: 'passives', label: 'Pasifler' },
      { id: 'quests', label: 'G\u00F6revler' },
      { id: 'map', label: 'Harita' },
      { id: 'settings', label: 'Ayarlar' },
      { id: 'save', label: 'Kaydet' },
    ];
  }

  private injectStyles(): void {
    if (this.styleInjected) return;
    const style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);
    this.styleInjected = true;
  }

  private render(): void {
    if (!this._isOpen) return;
    this.injectStyles();
    if (this.container) this.container.remove();

    this.container = document.createElement('div');
    this.container.className = 'gm-overlay';
    this.container.addEventListener('mousedown', (e) => {
      if (e.target === this.container) this.close();
    });

    const panel = document.createElement('div');
    panel.className = 'gm-panel';

    panel.appendChild(this.buildHeader());
    panel.appendChild(this.buildTabBar());
    panel.appendChild(this.buildContent());

    this.container.appendChild(panel);
    document.body.appendChild(this.container);
  }

  private buildHeader(): HTMLElement {
    const header = document.createElement('div');
    header.className = 'gm-header';

    const title = document.createElement('h2');
    title.textContent = 'Men\u00FC';
    header.appendChild(title);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'gm-close';
    closeBtn.textContent = '\u2715';
    closeBtn.onclick = () => this.close();
    header.appendChild(closeBtn);

    return header;
  }

  private buildTabBar(): HTMLElement {
    const tabBar = document.createElement('div');
    tabBar.className = 'gm-tabs';

    for (const tab of this.getTabs()) {
      const btn = document.createElement('button');
      btn.className = 'gm-tab' + (tab.id === this.activeTab ? ' active' : '');
      btn.textContent = tab.label;
      btn.onclick = () => {
        this.activeTab = tab.id;
        this.render();
      };
      tabBar.appendChild(btn);
    }

    return tabBar;
  }

  private buildContent(): HTMLElement {
    const content = document.createElement('div');
    content.className = 'gm-content';

    switch (this.activeTab) {
      case 'character': content.appendChild(this.buildCharacterTab()); break;
      case 'passives': content.appendChild(this.buildPassivesTab()); break;
      case 'quests': content.appendChild(this.buildQuestsTab()); break;
      case 'map': content.appendChild(this.buildMapTab()); break;
      case 'settings': content.appendChild(this.buildSettingsTab()); break;
      case 'save': content.appendChild(this.buildSaveTab()); break;
    }

    return content;
  }

  // ========================
  // Tab 1: Character Stats
  // ========================

  private buildCharacterTab(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'gm-char-grid';

    const p = this.player;

    // Left column: Character info + Attributes
    const left = document.createElement('div');

    // Character info
    const infoSection = this.createSection('Karakter Bilgisi');
    this.addStatRow(infoSection, 'S\u0131n\u0131f', this.getClassName(p.className));
    this.addStatRow(infoSection, 'Seviye', String(p.level));

    // XP bar
    const xpLabel = document.createElement('div');
    xpLabel.className = 'gm-stat-row';
    const xpLabelText = document.createElement('span');
    xpLabelText.className = 'gm-stat-label';
    xpLabelText.textContent = 'Deneyim';
    const xpVal = document.createElement('span');
    xpVal.className = 'gm-stat-value';
    xpVal.textContent = `${p.experience} / ${p.getExperienceForNextLevel()}`;
    xpLabel.appendChild(xpLabelText);
    xpLabel.appendChild(xpVal);
    infoSection.appendChild(xpLabel);

    const xpBar = document.createElement('div');
    xpBar.className = 'gm-xp-bar';
    const xpFill = document.createElement('div');
    xpFill.className = 'gm-xp-fill';
    xpFill.style.width = `${Math.min(100, p.getExperiencePercent() * 100)}%`;
    xpBar.appendChild(xpFill);
    infoSection.appendChild(xpBar);

    left.appendChild(infoSection);

    // Attributes
    const attrSection = this.createSection('Nitelikler');
    this.addStatRow(attrSection, 'G\u00FC\u00E7', String(p.stats.strength));
    this.addStatRow(attrSection, '\u00C7eviklik', String(p.stats.agility));
    this.addStatRow(attrSection, 'Ruh', String(p.stats.spirit));
    left.appendChild(attrSection);

    // Defense section in left column
    const defSection = this.createSection('Savunma');
    this.addStatRow(defSection, 'Maksimum Can', String(p.stats.maxHp));
    this.addStatRow(defSection, 'Maksimum Mana', String(p.stats.maxMana));
    this.addStatRow(defSection, 'Z\u0131rh', String(p.stats.armor));
    this.addStatRow(defSection, 'Ka\u00E7\u0131nma', String(p.stats.evasion));
    this.addStatRow(defSection, 'Blok \u015Eans\u0131', `${p.stats.blockChance ?? 0}%`);
    this.addStatRow(defSection, 'Enerji Kalkan\u0131', `${p.stats.energyShield} / ${p.stats.maxEnergyShield}`);
    left.appendChild(defSection);

    wrap.appendChild(left);

    // Right column: Offense + Resistances
    const right = document.createElement('div');

    // Offense
    const offSection = this.createSection('Sald\u0131r\u0131');
    this.addStatRow(offSection, 'Sald\u0131r\u0131 Hasar\u0131', String(p.attackDamage));
    this.addStatRow(offSection, 'Sald\u0131r\u0131 H\u0131z\u0131', `${p.attackSpeed.toFixed(2)}/sn`);
    left.querySelectorAll('.gm-section'); // no-op
    right.appendChild(offSection);

    // Resistances
    const resSection = this.createSection('Diren\u00E7ler');
    this.addResBar(resSection, 'Od', p.resistances.od, 75, '#e8443a');
    this.addResBar(resSection, 'Su', p.resistances.su, 75, '#4a9ef5');
    this.addResBar(resSection, 'G\u00F6k', p.resistances.gok, 75, '#f5d74a');
    this.addResBar(resSection, 'Karanl\u0131k', p.resistances.karanlik, 50, '#9b59b6');
    this.addResBar(resSection, 'Fiziksel', p.resistances.physical, 90, '#a09080');
    right.appendChild(resSection);

    wrap.appendChild(right);
    return wrap;
  }

  private getClassName(cn: string): string {
    const names: Record<string, string> = {
      alp: 'Alp (Sava\u015F\u00E7\u0131)',
      kam: 'Kam (\u015Eaman)',
      mergen: 'Mergen (Ok\u00E7u)',
      boru: 'B\u00F6r\u00FC (Hayvan \u00DCst.)',
    };
    return names[cn] ?? cn;
  }

  private createSection(title: string): HTMLElement {
    const section = document.createElement('div');
    section.className = 'gm-section';
    const h = document.createElement('div');
    h.className = 'gm-section-title';
    h.textContent = title;
    section.appendChild(h);
    return section;
  }

  private addStatRow(parent: HTMLElement, label: string, value: string): void {
    const row = document.createElement('div');
    row.className = 'gm-stat-row';
    const lbl = document.createElement('span');
    lbl.className = 'gm-stat-label';
    lbl.textContent = label;
    const val = document.createElement('span');
    val.className = 'gm-stat-value';
    val.textContent = value;
    row.appendChild(lbl);
    row.appendChild(val);
    parent.appendChild(row);
  }

  private addResBar(parent: HTMLElement, label: string, value: number, cap: number, color: string): void {
    const row = document.createElement('div');
    row.className = 'gm-res-row';

    const lbl = document.createElement('span');
    lbl.className = 'gm-res-label';
    lbl.textContent = label;
    row.appendChild(lbl);

    const bar = document.createElement('div');
    bar.className = 'gm-res-bar';
    const fill = document.createElement('div');
    fill.className = 'gm-res-fill';
    fill.style.width = `${Math.min(100, (value / cap) * 100)}%`;
    fill.style.background = color;
    bar.appendChild(fill);
    row.appendChild(bar);

    const val = document.createElement('span');
    val.className = 'gm-res-val';
    val.textContent = `${value} / ${cap}`;
    row.appendChild(val);

    parent.appendChild(row);
  }

  // ========================
  // Tab 2: Passives
  // ========================

  private buildPassivesTab(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:flex;flex-direction:column;height:100%;';

    const p = this.player;
    const points = p.passiveTree.getState().availablePoints;
    const branches = p.passiveTree.getSelectedBranches();
    const allocated = p.passiveTree.getState().allocatedNodes;

    // Tree always shown - branch selection happens by clicking branch_start nodes

    // Top info bar
    const infoBar = document.createElement('div');
    infoBar.style.cssText = 'display:flex;gap:16px;padding:8px 0;border-bottom:1px solid #3a3530;margin-bottom:8px;flex-shrink:0;flex-wrap:wrap;';

    const pointsBadge = document.createElement('span');
    pointsBadge.style.cssText = `color:#ffd700;font-weight:bold;font-size:14px;`;
    pointsBadge.textContent = `🔸 Kullanılabilir Puan: ${points}`;
    infoBar.appendChild(pointsBadge);

    const allocBadge = document.createElement('span');
    allocBadge.style.cssText = 'color:#8a7e6e;font-size:12px;';
    allocBadge.textContent = `Tahsis edilen: ${allocated.size}`;
    infoBar.appendChild(allocBadge);

    if (branches.length > 0) {
      const branchBadge = document.createElement('span');
      branchBadge.style.cssText = 'color:#8a7e6e;font-size:12px;';
      branchBadge.textContent = `Dallar: ${branches.join(', ')}`;
      infoBar.appendChild(branchBadge);
    }

    const bpBadge = document.createElement('span');
    bpBadge.style.cssText = `color:${p.passiveTree.getBranchPoints() > 0 ? '#ff8800' : '#555'};font-size:12px;`;
    bpBadge.textContent = `🌟 Başlangıç Puanı: ${p.passiveTree.getBranchPoints()}`;
    infoBar.appendChild(bpBadge);

    // Branch selection buttons (if level 10+ and branches not yet selected)
    if (p.level >= 10 && branches.length < 2) {
      const branchDiv = document.createElement('div');
      branchDiv.style.cssText = 'margin-left:auto;display:flex;gap:4px;';
      const branchNames: Record<string, string> = {
        demir: '⚔️ Demir', kale: '🏰 Kale', avci: '🏹 Avcı',
        golge: '🗡️ Gölge', firtina: '🌩️ Fırtına', ruhlar: '👻 Ruhlar',
      };
      const branchColors: Record<string, string> = {
        demir: '#b87333', kale: '#708090', avci: '#228b22',
        golge: '#6a0dad', firtina: '#3498db', ruhlar: '#8e44ad',
      };
      for (const [id, name] of Object.entries(branchNames)) {
        if (branches.includes(id as any)) continue;
        const btn = document.createElement('button');
        btn.style.cssText = `padding:4px 8px;background:#151310;border:1px solid ${branchColors[id]};color:${branchColors[id]};border-radius:4px;cursor:pointer;font-size:10px;`;
        btn.textContent = name;
        btn.title = `${name} dalını seç`;
        btn.onclick = () => {
          p.passiveTree.selectBranch(id as any);
          this.render();
        };
        branchDiv.appendChild(btn);
      }
      infoBar.appendChild(branchDiv);
    }

    wrap.appendChild(infoBar);

    // Canvas for tree rendering
    const canvasWrap = document.createElement('div');
    canvasWrap.style.cssText = 'flex:1;position:relative;overflow:hidden;border:1px solid #2a2520;border-radius:6px;background:#0a0908;';

    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'width:100%;height:100%;cursor:grab;';
    canvasWrap.appendChild(canvas);

    wrap.appendChild(canvasWrap);

    // Tooltip div
    const tooltip = document.createElement('div');
    tooltip.style.cssText = 'position:absolute;display:none;background:#1a1510;border:1px solid #ffd700;border-radius:6px;padding:8px 12px;pointer-events:none;z-index:10;max-width:250px;font-size:11px;color:#f4e8d4;box-shadow:0 4px 12px rgba(0,0,0,0.8);';
    canvasWrap.appendChild(tooltip);

    // Canvas will be rendered by redraw() below after setup

    // Pan & zoom - use class-level state so it persists across re-renders
    let isPanning = false, panStartX = 0, panStartY = 0, panScrollX = 0, panScrollY = 0;

    // Center on tree ONLY on first open
    const allNodes = p.passiveTree.getAllNodes();
    if (!this.ptInitialized && allNodes.length > 0) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const n of allNodes) {
        if (n.position.x < minX) minX = n.position.x;
        if (n.position.y < minY) minY = n.position.y;
        if (n.position.x > maxX) maxX = n.position.x;
        if (n.position.y > maxY) maxY = n.position.y;
      }
      const cx = (minX + maxX) / 2;
      const cy = (minY + maxY) / 2;
      this.ptScrollX = cx - 400;
      this.ptScrollY = cy - 300;
      this.ptInitialized = true;
    }

    // Alias for closure access
    const self = this;

    const redraw = () => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      ctx.scale(self.ptZoom, self.ptZoom);
      ctx.translate(-self.ptScrollX, -self.ptScrollY);
      self.drawTree(ctx, p, p.passiveTree.getState().allocatedNodes);
      ctx.restore();
    };

    let didPan = false;

    // Prevent context menu on canvas
    canvas.oncontextmenu = (e) => e.preventDefault();

    canvas.onmousedown = (e) => {
      e.preventDefault();
      if (e.button === 2) return; // Don't start pan on right click
      isPanning = true;
      didPan = false;
      panStartX = e.clientX;
      panStartY = e.clientY;
      panScrollX = self.ptScrollX;
      panScrollY = self.ptScrollY;
      canvas.style.cursor = 'grabbing';
    };

    canvas.onmousemove = (e) => {
      if (isPanning) {
        const dx = e.clientX - panStartX;
        const dy = e.clientY - panStartY;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didPan = true;
        self.ptScrollX = panScrollX - dx / self.ptZoom;
        self.ptScrollY = panScrollY - dy / self.ptZoom;
        tooltip.style.display = 'none';
        redraw();
        return;
      }

      // Hover tooltip
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const cx = (e.clientX - rect.left) * scaleX;
      const cy = (e.clientY - rect.top) * scaleY;
      const wx = self.ptScrollX + cx / self.ptZoom;
      const wy = self.ptScrollY + cy / self.ptZoom;

      let found = false;
      for (const node of allNodes) {
        const dx = node.position.x - wx;
        const dy = node.position.y - wy;
        const hr = node.type === 'keystone' ? 25 : node.type === 'notable' ? 18 : 15;
        if (dx * dx + dy * dy < hr * hr) {
          const isAlloc = p.passiveTree.getState().allocatedNodes.has(node.id);
          const bonusLines = Object.entries(node.bonuses)
            .filter(([, v]) => typeof v === 'number' && v !== 0)
            .map(([k, v]) => {
              const sign = (v as number) > 0 ? '+' : '';
              const label = k.replace('percent', '%').replace('flat', '').replace('Damage', ' Hasar').replace('Armor', ' Zırh').replace('Hp', ' Can').replace('Mana', ' Mana').replace('Regen', ' Yenileme').replace('Speed', ' Hız').replace('Chance', ' Şans');
              return `<div style="color:#88dd88">${sign}${v} ${label}</div>`;
            });

          let typeLabel = '';
          if (node.type === 'small') typeLabel = '● Küçük Düğüm';
          else if (node.type === 'notable') typeLabel = '◆ Önemli Düğüm';
          else if (node.type === 'keystone') typeLabel = '★ Kilit Taşı';
          else if (node.type === 'branch_start') typeLabel = '⬟ Başlangıç';
          else if (node.isAttributeNode) typeLabel = '◉ Nitelik Düğümü (+5 Güç/Çeviklik/Ruh)';

          let html = `<div style="font-weight:bold;color:#ffd700;margin-bottom:4px">${node.name}</div>`;
          html += `<div style="color:#888;font-size:10px;margin-bottom:4px">${typeLabel}</div>`;
          if (node.description) html += `<div style="color:#aaa;margin-bottom:4px">${node.description}</div>`;
          if (bonusLines.length > 0) html += bonusLines.join('');
          else if (node.isAttributeNode) html += `<div style="color:#ffd700">+5 Seçilen Nitelik</div>`;
          if (node.keystoneEffect) html += `<div style="color:#ff6666;margin-top:4px;font-style:italic">${node.keystoneEffect}</div>`;
          let actionText = '';
          if (isAlloc) {
            actionText = '✅ Tahsis edildi | Sağ tık: İade';
          } else if (node.type === 'branch_start') {
            const hasBP = p.passiveTree.getBranchPoints() > 0;
            const branchSelected = node.branchId && p.passiveTree.getSelectedBranches().includes(node.branchId as any);
            if (branchSelected) actionText = '✅ Dal seçili';
            else if (hasBP) actionText = '🌟 Tıkla: Bu dalı seç (1 başlangıç puanı harcar)';
            else actionText = '🔒 Başlangıç puanı gerekli';
          } else {
            actionText = '⬜ Tıkla: Tahsis et';
          }
          html += `<div style="color:#555;font-size:9px;margin-top:4px">${actionText}</div>`;

          tooltip.innerHTML = html;
          tooltip.style.display = 'block';
          tooltip.style.left = `${e.clientX - rect.left + 15}px`;
          tooltip.style.top = `${e.clientY - rect.top - 10}px`;
          canvas.style.cursor = isAlloc ? 'default' : 'pointer';
          found = true;
          break;
        }
      }
      if (!found) {
        tooltip.style.display = 'none';
        canvas.style.cursor = 'grab';
      }
    };

    canvas.onmouseup = (e) => {
      isPanning = false;
      canvas.style.cursor = 'grab';

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const canvasX = (e.clientX - rect.left) * scaleX;
      const canvasY = (e.clientY - rect.top) * scaleY;
      const wx = self.ptScrollX + canvasX / self.ptZoom;
      const wy = self.ptScrollY + canvasY / self.ptZoom;

      // Right click: refund node
      if (e.button === 2) {
        const currentAllocated = p.passiveTree.getState().allocatedNodes;
        for (const node of allNodes) {
          const ndx = node.position.x - wx;
          const ndy = node.position.y - wy;
          const hitRadius = node.type === 'keystone' ? 25 : node.type === 'notable' ? 18 : 15;
          if (ndx * ndx + ndy * ndy < hitRadius * hitRadius) {
            if (currentAllocated.has(node.id)) {
              const success = p.passiveTree.deallocateNode(node.id);
              if (success) {
                tooltip.style.display = 'none';
                p.recalculateStats();
                self.render();
              }
              return;
            }
          }
        }
        return;
      }

      // Left click: allocate node (only if didn't pan)
      if (!didPan && e.button === 0) {
        const currentPoints = p.passiveTree.getState().availablePoints;
        const currentAllocated = p.passiveTree.getState().allocatedNodes;

        for (const node of allNodes) {
          const ndx = node.position.x - wx;
          const ndy = node.position.y - wy;
          const hitRadius = node.type === 'keystone' ? 25 : node.type === 'notable' ? 18 : 15;
          if (ndx * ndx + ndy * ndy < hitRadius * hitRadius) {
            if (!currentAllocated.has(node.id)) {
              // Branch start node: select branch with branch point
              if (node.type === 'branch_start' && node.branchId) {
                const bp = p.passiveTree.getBranchPoints();
                if (bp > 0 && !p.passiveTree.getSelectedBranches().includes(node.branchId as any)) {
                  const success = p.passiveTree.selectBranchWithPoint(node.branchId as any);
                  if (success) {
                    p.passiveTree.getState().allocatedNodes.add(node.id);
                    p.recalculateStats();
                    self.render();
                  }
                }
                return;
              }

              // Normal allocate
              if (currentPoints > 0) {
                // Attribute node: show choice popup first
                if (node.isAttributeNode) {
                  self.showAttributeChoice(node.id, e.clientX, e.clientY, p, canvasWrap, redraw);
                  return;
                }
                const success = p.passiveTree.allocateNode(node.id);
                if (success) {
                  p.recalculateStats();
                  self.render();
                }
                return;
              }
            }
          }
        }
      }
    };

    canvas.onwheel = (e) => {
      e.preventDefault();
      const oldZoom = self.ptZoom;
      self.ptZoom = Math.max(0.2, Math.min(2.0, self.ptZoom + (e.deltaY > 0 ? -0.05 : 0.05)));
      const wx = self.ptScrollX + e.offsetX / oldZoom;
      const wy = self.ptScrollY + e.offsetY / oldZoom;
      self.ptScrollX = wx - e.offsetX / self.ptZoom;
      self.ptScrollY = wy - e.offsetY / self.ptZoom;
      redraw();
    };

    // Initial render
    requestAnimationFrame(() => {
      const rect2 = canvasWrap.getBoundingClientRect();
      canvas.width = rect2.width;
      canvas.height = rect2.height;
      redraw();
    });

    return wrap;
  }

  // Branch selection screen - kept for potential future use as modal option
  public buildBranchSelectionScreen(player: Player, selectionNum: number, alreadySelected: string[]): HTMLElement {
    const screen = document.createElement('div');
    screen.style.cssText = 'display:flex;flex-direction:column;align-items:center;height:100%;overflow-y:auto;padding:20px;';

    // Title
    const title = document.createElement('div');
    title.style.cssText = 'color:#ffd700;font-size:22px;font-weight:bold;margin-bottom:8px;text-shadow:0 0 10px rgba(255,215,0,0.3);';
    title.textContent = selectionNum === 1 ? '🌟 İlk Dalını Seç' : '🌟 İkinci Dalını Seç';
    screen.appendChild(title);

    const subtitle = document.createElement('div');
    subtitle.style.cssText = 'color:#8a7e6e;font-size:13px;margin-bottom:24px;text-align:center;max-width:500px;';
    subtitle.textContent = selectionNum === 1
      ? 'Pasif ağacın açıldı! Bir dal seçerek yolculuğuna başla. Bu seçim karakterinin temel yönelimini belirler.'
      : 'İkinci dalını seçerek build\'ini genişlet. İki dalın kesişim noktasında güçlü bonuslar keşfedebilirsin.';
    screen.appendChild(subtitle);

    // Branch cards grid
    const grid = document.createElement('div');
    grid.style.cssText = 'display:grid;grid-template-columns:repeat(3, 1fr);gap:12px;max-width:700px;width:100%;';

    const branchData = [
      { id: 'demir', name: 'Demir', icon: '⚔️', color: '#b87333', desc: 'Yakın dövüş ve fiziksel güç. Melee hasarı, saldırı hızı, fiziksel hasar bonusları.', stats: '+% Fiziksel Hasar, +% Saldırı Hızı' },
      { id: 'kale', name: 'Kale', icon: '🏰', color: '#708090', desc: 'Tank ve dayanıklılık. Zırh, can, blok şansı ve savunma bonusları.', stats: '+% Can, +% Zırh, +% Blok' },
      { id: 'avci', name: 'Avcı', icon: '🏹', color: '#228b22', desc: 'Menzilli saldırı ve hassasiyet. Mermi, kritik ve hız bonusları.', stats: '+% Kritik Şansı, +% Saldırı Hızı' },
      { id: 'golge', name: 'Gölge', icon: '🗡️', color: '#6a0dad', desc: 'Kaçınma ve zehir. DoT hasarı, ailment ve kaçınma bonusları.', stats: '+% Kaçınma, +% DoT Hasarı' },
      { id: 'firtina', name: 'Fırtına', icon: '🌩️', color: '#3498db', desc: 'Elemental büyü gücü. Büyü hasarı, alan etkisi ve cast hızı.', stats: '+% Büyü Hasarı, +% Elemental' },
      { id: 'ruhlar', name: 'Ruhlar', icon: '👻', color: '#8e44ad', desc: 'Enerji kalkanı ve çağırma. ES, minyon ve aura bonusları.', stats: '+% ES, +% Minyon Hasarı' },
    ];

    for (const branch of branchData) {
      const isDisabled = alreadySelected.includes(branch.id);
      const card = document.createElement('div');
      card.style.cssText = `
        background:linear-gradient(180deg, #1a1815, #0d0a08);
        border:2px solid ${isDisabled ? '#333' : branch.color + '66'};
        border-radius:10px;padding:16px;cursor:${isDisabled ? 'not-allowed' : 'pointer'};
        transition:all 0.2s;opacity:${isDisabled ? '0.4' : '1'};
        display:flex;flex-direction:column;align-items:center;text-align:center;gap:8px;
      `;

      if (!isDisabled) {
        card.onmouseenter = () => {
          card.style.borderColor = branch.color;
          card.style.boxShadow = `0 0 20px ${branch.color}33`;
          card.style.transform = 'translateY(-2px)';
        };
        card.onmouseleave = () => {
          card.style.borderColor = branch.color + '66';
          card.style.boxShadow = 'none';
          card.style.transform = 'none';
        };
        card.onclick = () => {
          const success = player.passiveTree.selectBranchWithPoint(branch.id as any);
          if (!success) return;
          // Allocate branch start node
          const bStart = player.passiveTree.getAllNodes().find(
            n => n.type === 'branch_start' && n.branchId === branch.id
          );
          if (bStart) player.passiveTree.getState().allocatedNodes.add(bStart.id);
          // Branch selected successfully
          if (true) {
          }
          player.recalculateStats();
          this.render();
        };
      }

      card.innerHTML = `
        <div style="font-size:36px">${branch.icon}</div>
        <div style="color:${branch.color};font-size:16px;font-weight:bold">${branch.name}</div>
        <div style="color:#aaa;font-size:11px;line-height:1.4">${branch.desc}</div>
        <div style="color:${branch.color};font-size:10px;font-weight:bold;margin-top:4px">${branch.stats}</div>
        ${isDisabled ? '<div style="color:#666;font-size:10px">Zaten seçili</div>' : ''}
      `;

      grid.appendChild(card);
    }

    screen.appendChild(grid);

    // Info text at bottom
    const info = document.createElement('div');
    info.style.cssText = 'color:#555;font-size:11px;margin-top:20px;text-align:center;';
    info.textContent = selectionNum === 1
      ? 'Seviye 40\'ta ikinci dalını seçebileceksin.'
      : 'İki dal seçildi. Artık her iki dalda da pasif puan harcayabilirsin.';
    screen.appendChild(info);

    return screen;
  }

  private showAttributeChoice(nodeId: string, mouseX: number, mouseY: number, player: Player, parent: HTMLElement, _redraw: () => void): void {
    // Remove any existing popup
    const old = parent.querySelector('.attr-choice-popup');
    if (old) old.remove();

    const popup = document.createElement('div');
    popup.className = 'attr-choice-popup';
    popup.style.cssText = `
      position:absolute;
      left:${mouseX - parent.getBoundingClientRect().left}px;
      top:${mouseY - parent.getBoundingClientRect().top - 120}px;
      background:linear-gradient(180deg, #1a1510, #0d0a08);
      border:2px solid #ffd700;
      border-radius:8px;
      padding:12px;
      z-index:20;
      box-shadow:0 4px 16px rgba(0,0,0,0.9);
      display:flex;
      flex-direction:column;
      gap:6px;
      min-width:160px;
    `;

    const title = document.createElement('div');
    title.style.cssText = 'color:#ffd700;font-weight:bold;font-size:12px;text-align:center;margin-bottom:4px;';
    title.textContent = 'Nitelik Seç (+5)';
    popup.appendChild(title);

    const attrs = [
      { id: 'strength', name: '💪 Güç', desc: '+10 Can, +%1 Melee Hasar', color: '#b87333' },
      { id: 'agility', name: '🦅 Çeviklik', desc: '+5 Kaçınma, +%1 Saldırı Hızı', color: '#228b22' },
      { id: 'spirit', name: '🔮 Ruh', desc: '+10 Mana, +%1 Enerji Kalkanı', color: '#8e44ad' },
    ];

    for (const attr of attrs) {
      const btn = document.createElement('button');
      btn.style.cssText = `
        display:flex;flex-direction:column;align-items:flex-start;
        padding:8px 12px;background:#151310;border:1px solid ${attr.color};
        border-radius:6px;cursor:pointer;color:#f4e8d4;font-size:12px;
        transition:background 0.15s;width:100%;text-align:left;
      `;
      btn.innerHTML = `<span style="font-weight:bold">${attr.name}</span><span style="font-size:9px;color:#888;margin-top:2px">${attr.desc}</span>`;

      btn.onmouseenter = () => { btn.style.background = '#2a2520'; };
      btn.onmouseleave = () => { btn.style.background = '#151310'; };

      btn.onclick = () => {
        const success = player.passiveTree.allocateNode(nodeId);
        if (success) {
          player.passiveTree.setAttributeChoice(nodeId, attr.id as 'strength' | 'agility' | 'spirit');
          player.recalculateStats();
        }
        popup.remove();
        this.render();
      };
      popup.appendChild(btn);
    }

    // Cancel button
    const cancelBtn = document.createElement('button');
    cancelBtn.style.cssText = 'padding:4px;background:none;border:1px solid #3a3530;border-radius:4px;color:#888;font-size:10px;cursor:pointer;margin-top:4px;';
    cancelBtn.textContent = 'İptal';
    cancelBtn.onclick = () => popup.remove();
    popup.appendChild(cancelBtn);

    // Close on click outside
    const closeHandler = (ev: MouseEvent) => {
      if (!popup.contains(ev.target as Node)) {
        popup.remove();
        document.removeEventListener('mousedown', closeHandler);
      }
    };
    setTimeout(() => document.addEventListener('mousedown', closeHandler), 10);

    parent.appendChild(popup);
  }

  private drawTree(ctx: CanvasRenderingContext2D, player: Player, allocated: Set<string>): void {
    const allNodes = player.passiveTree.getAllNodes();
    const branchColors: Record<string, string> = {
      demir: '#b87333', kale: '#708090', avci: '#228b22',
      golge: '#6a0dad', firtina: '#3498db', ruhlar: '#8e44ad',
    };
    const typeRadii: Record<string, number> = {
      small: 8, notable: 14, keystone: 20, branch_start: 16, center: 22, attribute: 10,
    };

    // Draw connections
    ctx.lineWidth = 2;
    for (const node of allNodes) {
      for (const connId of node.connections) {
        const other = allNodes.find(n => n.id === connId);
        if (!other) continue;
        // Only draw each connection once (alphabetical order)
        if (node.id > other.id) continue;

        const bothAllocated = allocated.has(node.id) && allocated.has(other.id);
        ctx.strokeStyle = bothAllocated ? '#ffd700' : '#333';
        ctx.lineWidth = bothAllocated ? 3 : 1.5;
        ctx.beginPath();
        ctx.moveTo(node.position.x, node.position.y);
        ctx.lineTo(other.position.x, other.position.y);
        ctx.stroke();
      }
    }

    // Attribute colors (POE style)
    const attrColors: Record<string, string> = {
      strength: '#c45c3e',  // Red-orange
      agility: '#3e9c3e',   // Green
      spirit: '#3e6ec4',    // Blue
    };

    // Pre-check which nodes can be allocated
    const canAllocSet = new Set<string>();
    const branchPts = player.passiveTree.getBranchPoints();
    const selectedBranches = player.passiveTree.getSelectedBranches();

    for (const node of allNodes) {
      if (allocated.has(node.id)) continue;
      // Branch start: allocatable if has branch points and branch not yet selected
      if (node.type === 'branch_start' && node.branchId) {
        if (branchPts > 0 && !selectedBranches.includes(node.branchId as any)) {
          canAllocSet.add(node.id);
        }
        continue;
      }
      if (player.passiveTree.canAllocate(node.id)) {
        canAllocSet.add(node.id);
      }
    }

    // Draw allocatable highlight rings BEHIND nodes
    for (const node of allNodes) {
      if (!canAllocSet.has(node.id)) continue;
      const x = node.position.x;
      const y = node.position.y;
      const r = (typeRadii[node.type] || 8) + 5;

      // Branch start gets bigger brighter ring
      const isBranchStart = node.type === 'branch_start';

      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.strokeStyle = isBranchStart ? '#ffd700cc' : '#ffd70088';
      ctx.lineWidth = isBranchStart ? 3 : 2;
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(x, y, r + (isBranchStart ? 5 : 3), 0, Math.PI * 2);
      ctx.strokeStyle = isBranchStart ? '#ffd70055' : '#ffd70033';
      ctx.lineWidth = isBranchStart ? 2 : 1;
      ctx.stroke();
    }

    // Draw nodes
    for (const node of allNodes) {
      const x = node.position.x;
      const y = node.position.y;
      const r = typeRadii[node.type] || 8;
      const isAlloc = allocated.has(node.id);
      const branchColor = branchColors[node.branchId || ''] || '#555';

      // Attribute node: special rendering
      if (node.isAttributeNode) {
        const attrChoice = player.passiveTree.getAttributeChoice(node.id);
        const attrColor = attrChoice ? attrColors[attrChoice] || '#888' : null;

        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);

        if (isAlloc && attrColor) {
          // Allocated with choice: filled with attribute color
          ctx.fillStyle = attrColor;
          ctx.fill();
          ctx.strokeStyle = attrColor;
          ctx.lineWidth = 2.5;
          ctx.stroke();
          // Bright inner glow
          ctx.beginPath();
          ctx.arc(x, y, r - 3, 0, Math.PI * 2);
          ctx.fillStyle = attrColor + '88';
          ctx.fill();
        } else {
          // Unallocated: small grey circle (POE style)
          ctx.fillStyle = '#2a2825';
          ctx.fill();
          ctx.strokeStyle = '#555550';
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }

        // Inner dot
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fillStyle = isAlloc && attrColor ? '#fff' : '#666';
        ctx.fill();

        // Label
        if (isAlloc && attrChoice) {
          const attrIcons: Record<string, string> = { strength: '💪', agility: '🦅', spirit: '🔮' };
          ctx.fillStyle = attrColor || '#888';
          ctx.font = '8px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(attrIcons[attrChoice] || '', x, y + r + 9);
        } else if (!isAlloc) {
          ctx.fillStyle = '#444';
          ctx.font = '7px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('+5', x, y + r + 9);
        }
        continue; // Skip normal rendering
      }

      // Normal nodes
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);

      if (isAlloc) {
        ctx.fillStyle = branchColor;
        ctx.fill();
        ctx.strokeStyle = '#ffd700';
        ctx.lineWidth = 3;
        ctx.stroke();
      } else {
        ctx.fillStyle = '#1a1815';
        ctx.fill();
        ctx.strokeStyle = branchColor + '88';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Type indicator rings
      if (node.type === 'notable') {
        ctx.strokeStyle = isAlloc ? '#6ab8ff' : '#4a9eff88';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(x, y, r + 3, 0, Math.PI * 2);
        ctx.stroke();
      } else if (node.type === 'keystone') {
        ctx.strokeStyle = isAlloc ? '#ff6666' : '#ff444488';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(x, y, r + 4, 0, Math.PI * 2);
        ctx.stroke();
      } else if (node.type === 'branch_start') {
        ctx.strokeStyle = '#ffd700';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(x, y, r + 3, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Icon/text inside
      ctx.fillStyle = isAlloc ? '#fff' : '#888';
      ctx.font = `${Math.max(8, r * 0.8)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      if (node.type === 'center') {
        ctx.fillText('✦', x, y);
      } else if (node.type === 'branch_start') {
        ctx.fillText('⬟', x, y);
      } else if (node.type === 'keystone') {
        ctx.fillText('★', x, y);
      } else if (node.type === 'notable') {
        ctx.fillText('◆', x, y);
      } else if (node.isAttributeNode) {
        ctx.fillText('◉', x, y);
      } else {
        ctx.fillText('●', x, y);
      }

      // Label
      ctx.fillStyle = isAlloc ? '#f4e8d4' : '#555';
      ctx.font = '9px sans-serif';
      ctx.fillText(node.name, x, y + r + 10);

      // Bonus text for allocated notable/keystone
      if (isAlloc && (node.type === 'notable' || node.type === 'keystone')) {
        const bonusTexts = Object.entries(node.bonuses)
          .filter(([, v]) => typeof v === 'number' && v !== 0)
          .map(([k, v]) => `+${v} ${k.replace('percent', '%').replace('flat', '')}`)
          .slice(0, 2);
        if (bonusTexts.length > 0) {
          ctx.fillStyle = '#aaa';
          ctx.font = '7px sans-serif';
          ctx.fillText(bonusTexts.join(', '), x, y + r + 19);
        }
      }
    }
  }

  // ========================
  // Tab 3: Quests
  // ========================

  private buildQuestsTab(): HTMLElement {
    const wrap = document.createElement('div');

    const ph = document.createElement('div');
    ph.className = 'gm-placeholder';
    ph.textContent = 'G\u00F6rev takibi - yak\u0131nda';
    const sub = document.createElement('div');
    sub.className = 'gm-placeholder-sub';
    sub.textContent = 'Aktif g\u00F6revleriniz burada g\u00F6r\u00FCnecek';
    ph.appendChild(sub);
    wrap.appendChild(ph);

    return wrap;
  }

  // ========================
  // Tab 4: Map
  // ========================

  private buildMapTab(): HTMLElement {
    const wrap = document.createElement('div');

    const ph = document.createElement('div');
    ph.className = 'gm-placeholder';
    ph.textContent = 'D\u00FCnya Haritas\u0131 - yak\u0131nda';
    const sub = document.createElement('div');
    sub.className = 'gm-placeholder-sub';
    sub.textContent = 'D\u00FCnya haritas\u0131 g\u00F6rselle\u015Ftirmesi yak\u0131nda eklenecek';
    ph.appendChild(sub);
    wrap.appendChild(ph);

    return wrap;
  }

  // ========================
  // Tab 5: Settings
  // ========================

  private buildSettingsTab(): HTMLElement {
    const wrap = document.createElement('div');
    const audio = getAudioManager();

    // Sound settings
    const soundGroup = document.createElement('div');
    soundGroup.className = 'gm-setting-group';
    const soundTitle = document.createElement('div');
    soundTitle.className = 'gm-setting-group-title';
    soundTitle.textContent = 'Ses';
    soundGroup.appendChild(soundTitle);

    this.addSlider(soundGroup, 'Ana Ses', Math.round(audio.getMasterVolume() * 100), (v) => {
      audio.setMasterVolume(v / 100);
      localStorage.setItem('arpg_master_volume', String(v / 100));
    });
    this.addSlider(soundGroup, 'M\u00FCzik', Math.round(audio.getMusicVolume() * 100), (v) => {
      audio.setMusicVolume(v / 100);
      localStorage.setItem('arpg_music_volume', String(v / 100));
    });
    this.addSlider(soundGroup, 'Efekt', Math.round(audio.getSfxVolume() * 100), (v) => {
      audio.setSfxVolume(v / 100);
      localStorage.setItem('arpg_sfx_volume', String(v / 100));
    });
    wrap.appendChild(soundGroup);

    // Language settings
    const langGroup = document.createElement('div');
    langGroup.className = 'gm-setting-group';
    const langTitle = document.createElement('div');
    langTitle.className = 'gm-setting-group-title';
    langTitle.textContent = 'Dil';
    langGroup.appendChild(langTitle);

    const langRow = document.createElement('div');
    langRow.className = 'gm-toggle-group';
    const currentLang = getCurrentLocale();

    const trBtn = document.createElement('button');
    trBtn.className = 'gm-toggle-btn' + (currentLang === 'tr' ? ' active' : '');
    trBtn.textContent = 'T\u00FCrk\u00E7e';
    trBtn.onclick = () => { setLocale('tr'); this.render(); };
    langRow.appendChild(trBtn);

    const enBtn = document.createElement('button');
    enBtn.className = 'gm-toggle-btn' + (currentLang === 'en' ? ' active' : '');
    enBtn.textContent = 'English';
    enBtn.onclick = () => { setLocale('en'); this.render(); };
    langRow.appendChild(enBtn);

    langGroup.appendChild(langRow);
    wrap.appendChild(langGroup);

    // Display settings
    const displayGroup = document.createElement('div');
    displayGroup.className = 'gm-setting-group';
    const displayTitle = document.createElement('div');
    displayTitle.className = 'gm-setting-group-title';
    displayTitle.textContent = 'Ekran';
    displayGroup.appendChild(displayTitle);

    const fpsCheck = document.createElement('label');
    fpsCheck.className = 'gm-check-row';
    const fpsInput = document.createElement('input');
    fpsInput.type = 'checkbox';
    fpsInput.checked = localStorage.getItem('arpg_show_fps') === 'true';
    fpsInput.onchange = () => {
      localStorage.setItem('arpg_show_fps', String(fpsInput.checked));
    };
    fpsCheck.appendChild(fpsInput);
    const fpsLabel = document.createElement('span');
    fpsLabel.textContent = 'FPS G\u00F6ster';
    fpsCheck.appendChild(fpsLabel);
    displayGroup.appendChild(fpsCheck);

    wrap.appendChild(displayGroup);

    return wrap;
  }

  private addSlider(parent: HTMLElement, label: string, value: number, onChange: (v: number) => void): void {
    const row = document.createElement('div');
    row.className = 'gm-slider-row';

    const lbl = document.createElement('span');
    lbl.className = 'gm-slider-label';
    lbl.textContent = label;
    row.appendChild(lbl);

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.className = 'gm-range';
    slider.min = '0';
    slider.max = '100';
    slider.value = String(value);
    row.appendChild(slider);

    const val = document.createElement('span');
    val.className = 'gm-slider-val';
    val.textContent = String(value);
    row.appendChild(val);

    slider.oninput = () => {
      const v = parseInt(slider.value, 10);
      val.textContent = String(v);
      onChange(v);
    };

    parent.appendChild(row);
  }

  // ========================
  // Tab 6: Save/Load
  // ========================

  private buildSaveTab(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'gm-save-slots';

    // Auto-save slot
    wrap.appendChild(this.buildSaveSlot(-1, 'Otomatik Kayıt'));

    // 3 manual slots
    for (let i = 0; i < 3; i++) {
      wrap.appendChild(this.buildSaveSlot(i, `Kay\u0131t ${i + 1}`));
    }

    return wrap;
  }

  private buildSaveSlot(slotIndex: number, title: string): HTMLElement {
    const slot = document.createElement('div');
    slot.className = 'gm-save-slot';

    const titleEl = document.createElement('div');
    titleEl.className = 'gm-save-slot-title';
    titleEl.textContent = title;
    slot.appendChild(titleEl);

    // Try to load save info from localStorage
    const saveKey = slotIndex === -1 ? 'arpg_autosave' : `arpg_save_${slotIndex}`;
    const saveData = localStorage.getItem(saveKey);

    if (saveData) {
      try {
        const parsed = JSON.parse(saveData);
        const info = document.createElement('div');
        info.className = 'gm-save-slot-info';
        const charName = parsed.characterName || parsed.player?.className || 'Bilinmeyen';
        const level = parsed.player?.level ?? '?';
        info.innerHTML = [
          `<div>Karakter: <strong>${charName}</strong></div>`,
          `<div>Seviye: <strong>${level}</strong></div>`,
          parsed.playTime ? `<div>S\u00FCre: <strong>${this.formatPlayTime(parsed.playTime)}</strong></div>` : '',
          parsed.timestamp ? `<div>Tarih: <strong>${new Date(parsed.timestamp).toLocaleDateString()}</strong></div>` : '',
        ].join('');
        slot.appendChild(info);
      } catch {
        const empty = document.createElement('div');
        empty.className = 'gm-save-slot-empty';
        empty.textContent = 'Bo\u015F Kay\u0131t';
        slot.appendChild(empty);
      }
    } else {
      const empty = document.createElement('div');
      empty.className = 'gm-save-slot-empty';
      empty.textContent = 'Bo\u015F Kay\u0131t';
      slot.appendChild(empty);
    }

    // Action buttons
    const actions = document.createElement('div');
    actions.className = 'gm-save-slot-actions';

    if (slotIndex >= 0) {
      const saveBtn = document.createElement('button');
      saveBtn.className = 'gm-btn primary';
      saveBtn.textContent = 'Kaydet';
      saveBtn.onclick = () => {
        if (this.saveCallback) {
          this.saveCallback(slotIndex);
          this.render(); // Refresh to show updated slot
        }
      };
      actions.appendChild(saveBtn);
    }

    if (saveData) {
      const loadBtn = document.createElement('button');
      loadBtn.className = 'gm-btn';
      loadBtn.textContent = 'Y\u00FCkle';
      loadBtn.onclick = () => {
        if (this.loadCallback) {
          this.loadCallback(slotIndex);
          this.close();
        }
      };
      actions.appendChild(loadBtn);
    }

    slot.appendChild(actions);
    return slot;
  }

  private formatPlayTime(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}sa ${m}dk`;
    return `${m}dk`;
  }
}
